/**
 * WebSocket Provider - управление WebSocket соединением и интеграция с React Query
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { CacheLookupPolicy, InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiTokenRequest } from '../auth/authConfig';
import { WebSocketService } from '../services/core/WebSocketService';
import { WebSocketContext } from '../contexts/WebSocketContext';
import { useAppStore } from '../stores/useAppStore';
import { resolveWebSocketUrl } from './resolveWebSocketUrl';
import { useFeatureFlag } from '../hooks/useFeatureFlags';
import { logger } from '../utils/logger';
import { WS_ENTITY_TYPE_CONFIG, WS_TABLE_ID_NAMES } from '../services/core/wsNotificationConfig';
import { queryKeyFactory } from '../services/core/queryKeys';
import { hasAnyDirtyTables } from '../components/handsontable/tracking';
import type {
  WebSocketStatus,
  WebSocketEventType,
  DataUpdatedPayload,
  ConnectedPayload,
  StagePresencePayload,
} from '../types/websocket.types';

/**
 * Props для WebSocketProvider
 */
interface WebSocketProviderProps {
  children: React.ReactNode;
  url?: string;
  autoConnect?: boolean;
  showNotifications?: boolean | 'minimal' | 'all';
}

/**
 * WebSocket Provider Component
 */
export function WebSocketProvider({ 
  children, 
  url,
  autoConnect = true,
  showNotifications = 'all',
}: WebSocketProviderProps) {
  const queryClient = useQueryClient();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();
  // Идентичность для отсева self-echo от WS DATA_UPDATED: backend эхо-шлёт
  // событие обратно саверу, а у него уже отрабатывает invalidate в mutation.onSuccess.
  // Без фильтра получаем двойной refetch и отмену in-flight GET'а на тяжёлых таблицах
  // (например, Stage 5 dynamic-plan) с ошибкой "Запрос был отменен".
  const selfIdentityRef = useRef<{ upn?: string; name?: string }>({});
  useEffect(() => {
    selfIdentityRef.current = {
      upn: account?.username || undefined,
      name: account?.name || undefined,
    };
  }, [account?.username, account?.name]);
  const addNotification = useAppStore((state) => state.addNotification);

  // Центр уведомлений (колокольчик) временно скрыт за флагом notification_center.
  // Пока он выключен — не копим записи в истории: пользователю негде их увидеть.
  // Ref, чтобы смена флага не пересоздавала WS-обработчики (как selfIdentityRef).
  const { isEnabled: notificationCenterEnabled } = useFeatureFlag('notification_center');
  const notificationCenterEnabledRef = useRef(false);
  useEffect(() => {
    notificationCenterEnabledRef.current = notificationCenterEnabled;
  }, [notificationCenterEnabled]);

  const invalidationTimersRef = useRef(new Map<string, NodeJS.Timeout>());
  
  const [tokenReady, setTokenReady] = useState(false);

  /**
   * Тихо получает токен PLNG API для WebSocket аутентификации.
   * MSAL отдаёт токен из кэша, пока он действует, и сам обновляет его через
   * refresh token после истечения. Поэтому вызываем при КАЖДОМ (пере)подключении:
   * закэшированный один раз JWT живёт ~1 час, и бэкенд отклоняет его (jwt expired).
   */
  const acquireApiToken = useCallback(async (): Promise<string | null> => {
    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
      logger.debug('WebSocket: No MSAL accounts yet, waiting for auth', { category: 'api' });
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account: accounts[0],
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Don't attempt redirect from WebSocket — the main auth flow (useAuth) handles it.
        // WebSocket will reconnect automatically after the user re-authenticates.
        logger.warn('WebSocket: refresh token expired, waiting for main auth redirect...', { category: 'api' });
        return null;
      }

      logger.error('Failed to acquire token for WebSocket', { category: 'api', data: error });
      return null;
    }
  }, [instance]);

  /**
   * Сервис создаётся только после того, как токен получен впервые (пользователь вошёл).
   */
  useEffect(() => {
    if (tokenReady || inProgress !== 'none') return;

    let cancelled = false;
    acquireApiToken().then((token) => {
      if (!cancelled && token) setTokenReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [acquireApiToken, isAuthenticated, inProgress, tokenReady]);

  const wsUrl = useMemo(() => {
    const configuredUrl = url || import.meta.env.VITE_WS_URL;

    try {
      const resolvedUrl = resolveWebSocketUrl(window.location.origin, configuredUrl);
      if (!configuredUrl) {
        logger.debug('WebSocket: Using application-origin fallback URL', {
          category: 'api',
          data: { url: resolvedUrl },
        });
      }
      return resolvedUrl;
    } catch (error) {
      logger.error('WebSocket: Invalid URL configuration', { category: 'api', data: error });
      return null;
    }
  }, [url]);

  const [service, setService] = useState<WebSocketService | null>(null);
  const [status, setStatus] = useState<WebSocketStatus>('DISCONNECTED' as WebSocketStatus);


  /**
   * Инициализация WebSocket Service — создаётся ОДИН РАЗ при наличии URL и первого токена.
   * Обновления токена НЕ пересоздают сервис: при каждом (пере)подключении
   * сервис сам запрашивает свежий токен через getToken (acquireApiToken).
   */
  useEffect(() => {
    if (!wsUrl || !tokenReady) {
      logger.debug('WebSocket: Waiting for URL and token...', { 
        category: 'api', 
        data: { wsUrl, tokenReady } 
      });
      return;
    }
    
    logger.debug('WebSocket: Initializing service...', { 
      category: 'api',
      data: { 
        url: wsUrl,
        mode: import.meta.env.MODE,
        isDev: import.meta.env.DEV
      }
    });
    
    const wsService = new WebSocketService({
      url: wsUrl,
      getToken: acquireApiToken,
      autoConnect,
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      heartbeatInterval: 60000,
    });

    wsService.onStatusChange((newStatus) => {
      setStatus(newStatus);
    });

    setService(wsService);

    return () => {
      wsService.disconnect();
    };
  }, [wsUrl, tokenReady, autoConnect, acquireApiToken]);
  
  /**
   * Очистка таймеров инвалидации при unmount
   */
  useEffect(() => {
    const invalidationTimers = invalidationTimersRef.current;
    return () => {
      invalidationTimers.forEach(timer => clearTimeout(timer));
      invalidationTimers.clear();
    };
  }, []);

  // Консистентность обеспечивается через WS-события (DATA_UPDATED) и reconnect invalidation.
  // Периодическая инвалидация всех запросов убрана — она вызывала видимые перерисовки UI.

  /**
   * Обработчик DATA_UPDATED события
   */
  const handleDataUpdated = useCallback((payload: unknown) => {
    const data = payload as DataUpdatedPayload;
    
    logger.info('Data updated event received', {
      category: 'api',
      data: {
        queryKey: data.queryKey,
        tableId: data.tableId,
        entityType: data.entityType,
        action: data.action,
        userName: data.userName,
      }
    });

    // Инвалидация UI конфигурации по entityType (без уведомления пользователю)
    if (data.entityType === 'ui-config') {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.uiConfig.all });
      return;
    }

    // Смена методологии этапов 2–4: перечитываем её; данные этапов перечитает
    // useRefetchOnCalcMethodologyChange, увидев новое значение. Без self-echo
    // фильтра — повторный GET дешёвый и подтягивает автора смены.
    if (data.entityType === 'calc-methodology') {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.calcMethodology.all });
      return;
    }

    // Self-echo filter: если изменения прислал сам текущий юзер — пропускаем
    // инвалидацию (его React Query уже обновился через mutation.onSuccess).
    // Это устраняет двойной refetch и отмену первого запроса через 5s, которая
    // на тяжёлых GET'ах (Stage 5 dynamic-plan) всплывала как "Запрос был отменен".
    const self = selfIdentityRef.current;
    const normalizedSender = data.userName?.trim().toLocaleLowerCase();
    const normalizedUpn = self.upn?.trim().toLocaleLowerCase();
    const normalizedName = self.name?.trim().toLocaleLowerCase();
    const isSelfEcho =
      !!normalizedSender &&
      ((normalizedUpn && normalizedSender === normalizedUpn) ||
        (normalizedName && normalizedSender === normalizedName));
    if (isSelfEcho) {
      logger.debug('Skipped self-echo WS invalidation', {
        category: 'query',
        data: { queryKey: data.queryKey, userName: data.userName },
      });
      return;
    }

    if (data.queryKey && Array.isArray(data.queryKey) && data.queryKey.length > 0) {
      const queryKeyStr = JSON.stringify(data.queryKey);
      
      if (invalidationTimersRef.current.has(queryKeyStr)) {
        clearTimeout(invalidationTimersRef.current.get(queryKeyStr)!);
      }
      
      const timer = setTimeout(() => {
        // Если у пользователя есть несохранённые правки в ЛЮБОЙ таблице,
        // НЕ делаем active refetch — иначе Handsontable перерисуется
        // свежими данными и сотрёт локальные изменения. Помечаем stale
        // без рефетча; свежие данные подтянутся при следующем ручном reload.
        const dirty = hasAnyDirtyTables();
        const refetchType: 'active' | 'none' = dirty ? 'none' : 'active';

        queryClient.invalidateQueries({
          queryKey: data.queryKey,
          refetchType,
        }).then(() => {
          logger.debug('Invalidated queries', { category: 'query', data: { queryKey: data.queryKey, refetchType, skippedRefetch: dirty } });
        }).catch((error) => {
          logger.error('Invalidation failed', { category: 'query', data: { queryKey: data.queryKey, error } });
        });

        invalidationTimersRef.current.delete(queryKeyStr);
      }, 5000);
      
      invalidationTimersRef.current.set(queryKeyStr, timer);

      if (showNotifications !== false && data.userName && notificationCenterEnabledRef.current) {
        const config = data.entityType ? WS_ENTITY_TYPE_CONFIG[data.entityType] : null;

        let tableName = 'Таблица';
        if (data.tableId && WS_TABLE_ID_NAMES[data.tableId]) {
          tableName = WS_TABLE_ID_NAMES[data.tableId];
        } else if (config?.name) {
          tableName = config.name;
        }

        const notificationLink = config ? { path: config.path, label: config.label } : undefined;

        addNotification({
          type: 'data-update',
          title: `${tableName} обновлена`,
          message: `${data.userName} внёс изменения`,
          link: notificationLink,
          metadata: {
            queryKey: data.queryKey,
            tableId: data.tableId,
            entityType: data.entityType,
            action: data.action,
          },
        });
      }
    }
  }, [queryClient, showNotifications, invalidationTimersRef, addNotification]);

  /**
   * Обработчик CONNECTED события
   */
  const handleConnected = useCallback((payload: unknown) => {
    const data = payload as ConnectedPayload;
    logger.info('WebSocket connected', { category: 'api', data });
    // При реконнекте инвалидируем все active queries для гарантии свежести данных
    // (WS-события могли быть пропущены во время разрыва)
    queryClient.invalidateQueries({ refetchType: 'active' });
  }, [queryClient]);

  /**
   * Обработчик ERROR события
   */
  const handleError = useCallback((payload: unknown) => {
    const data = payload as { message?: string };
    
    logger.error('WebSocket error', { category: 'api', data });
    
    invalidationTimersRef.current.forEach(timer => clearTimeout(timer));
    invalidationTimersRef.current.clear();
    
    if (showNotifications !== false && notificationCenterEnabledRef.current) {
      addNotification({
        type: 'error',
        title: 'Проблема с подключением',
        message: data.message || 'Попытка переподключения...',
        metadata: {
          error: data.message,
        },
      });
    }
  }, [showNotifications, invalidationTimersRef, addNotification]);

  /**
   * Обработчик STAGE_PRESENCE / USER_JOINED_STAGE / USER_LEFT_STAGE событий
   * Обновляет кэш React Query напрямую — без HTTP-запроса
   */
  const handleStagePresence = useCallback((payload: unknown) => {
    const data = payload as StagePresencePayload;
    if (data.stage && Array.isArray(data.users)) {
      queryClient.setQueryData(['presence', data.stage], data.users);
      logger.debug('Stage presence updated', { category: 'api', data: { stage: data.stage, count: data.users.length } });
    }
  }, [queryClient]);

  /**
   * Регистрация обработчиков событий
   */
  const handlersRef = React.useRef({
    handleDataUpdated,
    handleConnected,
    handleError,
    handleStagePresence,
  });

  useEffect(() => {
    handlersRef.current = {
      handleDataUpdated,
      handleConnected,
      handleError,
      handleStagePresence,
    };
  }, [handleDataUpdated, handleConnected, handleError, handleStagePresence]);

  useEffect(() => {
    if (!service) return;

    logger.debug('WebSocketProvider: Подписываемся на события (service change)', { category: 'api' });

    const wrappedDataUpdated = (payload: unknown) => handlersRef.current.handleDataUpdated(payload);
    const wrappedConnected = (payload: unknown) => handlersRef.current.handleConnected(payload);
    const wrappedError = (payload: unknown) => handlersRef.current.handleError(payload);
    const wrappedStagePresence = (payload: unknown) => handlersRef.current.handleStagePresence(payload);

    service.on('DATA_UPDATED' as WebSocketEventType, wrappedDataUpdated);
    service.on('DATA_CREATED' as WebSocketEventType, wrappedDataUpdated);
    service.on('DATA_DELETED' as WebSocketEventType, wrappedDataUpdated);
    service.on('CONNECTED' as WebSocketEventType, wrappedConnected);
    service.on('ERROR' as WebSocketEventType, wrappedError);
    service.on('STAGE_PRESENCE' as WebSocketEventType, wrappedStagePresence);
    service.on('USER_JOINED_STAGE' as WebSocketEventType, wrappedStagePresence);
    service.on('USER_LEFT_STAGE' as WebSocketEventType, wrappedStagePresence);

    return () => {
      logger.debug('WebSocketProvider: Отписываемся от событий (cleanup)', { category: 'api' });
      service.off('DATA_UPDATED' as WebSocketEventType, wrappedDataUpdated);
      service.off('DATA_CREATED' as WebSocketEventType, wrappedDataUpdated);
      service.off('DATA_DELETED' as WebSocketEventType, wrappedDataUpdated);
      service.off('CONNECTED' as WebSocketEventType, wrappedConnected);
      service.off('ERROR' as WebSocketEventType, wrappedError);
      service.off('STAGE_PRESENCE' as WebSocketEventType, wrappedStagePresence);
      service.off('USER_JOINED_STAGE' as WebSocketEventType, wrappedStagePresence);
      service.off('USER_LEFT_STAGE' as WebSocketEventType, wrappedStagePresence);
    };
  }, [service]);

  /**
   * Переподключение
   */
  const reconnect = useCallback(() => {
    if (service) {
      logger.info('Manual reconnect triggered', { category: 'api' });
      service.disconnect();
      setTimeout(() => service.connect(), 500);
    }
  }, [service]);

  /**
   * Отключение
   */
  const disconnect = useCallback(() => {
    if (service) {
      logger.info('Manual disconnect triggered', { category: 'api' });
      service.disconnect();
    }
  }, [service]);

  /**
   * Context value
   */
  const contextValue = useMemo(() => ({
    service,
    status,
    isConnected: service?.isConnected() ?? false,
    reconnect,
    disconnect,
  }), [service, status, reconnect, disconnect]);

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}
